import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Mail, 
  Phone, 
  ArrowRight, 
  CheckCircle, 
  Sparkles, 
  Hammer,
  ShieldAlert,
  ArrowLeft,
  User
} from 'lucide-react';
import Header from '../header';
import Footer from '../Footer';
import config from '../../config';
import ReCAPTCHA from "react-google-recaptcha";

const Working = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [mobile, setMobile] = useState('');
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [formError, setFormError] = useState(null);

  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[6-9]\d{9}$/;

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!email) {
      newErrors.email = 'Email address is required';
    } else if (!emailRegex.test(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!mobile) {
      newErrors.mobile = 'Mobile number is required';
    } else if (!mobileRegex.test(mobile)) {
      newErrors.mobile = 'Please enter a valid 10-digit Indian mobile number';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      if (!captchaToken) {
        setFormError('Please verify you are human.');
        return;
      }
      setFormError(null);
      setIsSubmitting(true);

      try {
        const payload = {
          name: name.trim(),
          mobile: mobile,
          email: email,
          timestamp: new Date().toISOString(),
          recaptcha_token: captchaToken
        };
        
        const response = await fetch(`${config.API_URL}/requests/`, {
          method: 'POST',
          headers: {
            'accept': 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (response.ok) {
          setIsSubmitting(false);
          setIsSubmitted(true);
        } else {
          setFormError('Failed to submit request. Please try again.');
          setIsSubmitting(false);
        }
      } catch (error) {
        setFormError('Network error. Please try again.');
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-[#020410] text-slate-100 font-sans selection:bg-cyan-500 selection:text-black overflow-x-hidden">
      {/* Global Navigation Header */}
      <Header />

      {/* Hero Content Section */}
      <div className="relative pt-16 pb-8 overflow-hidden">
        {/* Glow Effects */}
        <div className="absolute top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        <div className="absolute top-1/3 right-1/4 translate-x-1/2 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto space-y-4">
            <div className="inline-flex items-center space-x-2 bg-indigo-950/40 border border-indigo-800/30 px-4 py-1.5 rounded-full">
              <Hammer className="w-4 h-4 text-indigo-400" />
              <span className="text-xs font-black uppercase tracking-[0.2em] text-indigo-400 font-mono">
                Portal In Development
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white leading-tight">
              We are working <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-indigo-500 bg-clip-text text-transparent">right here</span>
            </h1>
            <p className="text-slate-400 text-sm sm:text-base leading-relaxed">
              This digital dashboard is getting final touch-ups to offer a secure, premium sovereign transaction experience. In the meantime, our professional advisory representatives are available offline to handle all request pipelines immediately.
            </p>
          </div>
        </div>
      </div>

      {/* Form and CTA section */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 relative z-10">
        <div className="max-w-xl mx-auto">
          <div className="bg-gradient-to-b from-[#060e2b] to-[#04081c] border border-white/10 rounded-[2.5rem] p-6 sm:p-10 shadow-2xl relative overflow-hidden">
            {/* Corner glowing element */}
            <div className="absolute -top-16 -right-16 w-32 h-32 bg-cyan-500/20 rounded-full blur-2xl pointer-events-none" />

            <AnimatePresence mode="wait">
              {!isSubmitted ? (
                /* Callback Request Form */
                <motion.div
                  key="form"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -15 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-6"
                >
                  <div className="space-y-2 text-center sm:text-left">
                    <h3 className="text-2xl font-black text-white">Representative Contact Form</h3>
                    <p className="text-xs text-slate-400">
                      Submit your email and mobile number below. A Nerus representative will reach out to assist you shortly.
                    </p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    {/* Name Field */}
                    <div className="space-y-2">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                        Full Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <User className="w-4.5 h-4.5 text-slate-500" />
                        </div>
                        <input
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Your full name"
                          className={`w-full bg-slate-950/50 border ${
                            errors.name ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-cyan-400'
                          } rounded-2xl py-3.5 pl-12 pr-4 text-white text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-cyan-400/25 transition-all`}
                        />
                      </div>
                      {errors.name && (
                        <p className="text-red-400 text-[10px] font-semibold">{errors.name}</p>
                      )}
                    </div>

                    {/* Mobile Number Field */}
                    <div className="space-y-2">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                        Mobile Number
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <span className="text-slate-500 font-bold font-mono text-sm">+91</span>
                        </div>
                        <input
                          type="tel"
                          maxLength="10"
                          value={mobile}
                          onChange={(e) => setMobile(e.target.value.replace(/\D/g, ''))}
                          placeholder="Enter 10-digit mobile number"
                          className={`w-full bg-slate-950/50 border ${
                            errors.mobile ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-cyan-400'
                          } rounded-2xl py-3.5 pl-14 pr-4 text-white text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-cyan-400/25 transition-all font-mono`}
                        />
                      </div>
                      {errors.mobile && (
                        <p className="text-red-400 text-[10px] font-semibold">{errors.mobile}</p>
                      )}
                    </div>

                    {/* Email Field */}
                    <div className="space-y-2">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                        Email Address
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                          <Mail className="w-4.5 h-4.5 text-slate-500" />
                        </div>
                        <input
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="name@company.com"
                          className={`w-full bg-slate-950/50 border ${
                            errors.email ? 'border-red-500/50 focus:border-red-500' : 'border-white/10 focus:border-cyan-400'
                          } rounded-2xl py-3.5 pl-12 pr-4 text-white text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-cyan-400/25 transition-all`}
                        />
                      </div>
                      {errors.email && (
                        <p className="text-red-400 text-[10px] font-semibold">{errors.email}</p>
                      )}
                    </div>

                    {/* ReCAPTCHA */}
                    <div className="flex flex-col items-center pt-2 space-y-2">
                      <ReCAPTCHA
                        sitekey={config.RECAPTCHA_SITE_KEY}
                        onChange={(token) => {
                          setCaptchaToken(token);
                          if (formError) setFormError(null);
                        }}
                        theme="dark"
                      />
                      {formError && (
                        <p className="text-red-400 text-[10px] font-semibold text-center">{formError}</p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-black font-extrabold text-sm py-4 px-6 rounded-2xl flex items-center justify-center space-x-2 transition-all duration-300 shadow-lg shadow-cyan-500/20 disabled:opacity-50 select-none cursor-pointer mt-4"
                    >
                      {isSubmitting ? (
                        <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <span>Request Callback</span>
                          <ArrowRight className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </form>
                </motion.div>
              ) : (
                /* Success Message */
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  className="text-center py-6 space-y-6"
                >
                  <div className="w-16 h-16 bg-cyan-950/50 border border-cyan-500/30 rounded-full flex items-center justify-center mx-auto text-cyan-400">
                    <CheckCircle className="w-8 h-8" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-2xl font-black text-white">Details Submitted!</h3>
                    <p className="text-xs text-slate-400 px-4">
                      Thank you for submitting your details. Our relationship representative will get in touch with you shortly at <span className="text-cyan-400 font-semibold">{mobile}</span> or via <span className="text-cyan-400 font-semibold">{email}</span>.
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setIsSubmitted(false);
                      setName('');
                      setEmail('');
                      setMobile('');
                      setCaptchaToken(null);
                    }}
                    className="inline-flex items-center space-x-2 text-xs text-cyan-400 hover:text-cyan-300 transition-colors font-bold uppercase tracking-wider font-mono mt-2 cursor-pointer"
                  >
                    <ArrowLeft className="w-3.5 h-3.5" />
                    <span>Submit another request</span>
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Quick links to active services */}
          <div className="mt-12 text-center space-y-4">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">
              Explore Available Sovereign Portals
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a 
                href="/mutualfunds" 
                className="px-4 py-2 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all text-xs font-bold text-slate-300 hover:text-white"
              >
                Mutual Funds
              </a>
              <a 
                href="/demataccount" 
                className="px-4 py-2 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all text-xs font-bold text-slate-300 hover:text-white"
              >
                Demat Account
              </a>
              <a 
                href="/insurance" 
                className="px-4 py-2 rounded-xl bg-white/[0.02] border border-white/5 hover:border-cyan-500/30 hover:bg-white/[0.04] transition-all text-xs font-bold text-slate-300 hover:text-white"
              >
                Insurance Portal
              </a>
            </div>
          </div>
        </div>
      </main>

      {/* Global Footer */}
      <Footer />
    </div>
  );
};

export default Working;
